import type { QueryParamsList, QueryParamsRetrieve } from '../query'
import type { ListResponse, Resource, ResourceCreate, ResourceId, ResourceRel, ResourceSort, /* ResourceFilter */ ResourcesConfig, ResourceUpdate, } from '../resource'
import { ApiResource } from '../resource'

import type { Address, AddressType } from './addresses'
import type { Attachment } from './attachments'
import type { EventStore } from './event_stores'
import type { InventoryReturnLocation } from './inventory_return_locations'
import type { InventoryStockLocation } from './inventory_stock_locations'
import type { StockItem } from './stock_items'
import type { StockTransfer } from './stock_transfers'
import type { Store } from './stores'
import type { Version } from './versions'


type StockLocationType = 'stock_locations'
type StockLocationRel = ResourceRel & { type: StockLocationType }
type AddressRel = ResourceRel & { type: AddressType }


export type StockLocationSort = Pick<StockLocation, 'id' | 'name' | 'code' | 'label_format' | 'suppress_etd'> & ResourceSort
// export type StockLocationFilter = Pick<StockLocation, 'id' | 'name' | 'code' | 'label_format' | 'suppress_etd'> & ResourceFilter


interface StockLocation extends Resource {
	
	readonly type: StockLocationType

	/** 
	 * Unique identifier for the stock location (numeric).
	 * @example ```1234```
	 */
	number?: number | null
	/** 
	 * The stock location's internal name.
	 * @example ```"Primary warehouse"```
	 */
	name: string
	/** 
	 * A string that you can use to identify the stock location (must be unique within the environment).
	 * @example ```"europe1"```
	 */
	code?: string | null
	/** 
	 * The shipping label format for this stock location. Can be one of 'PDF', 'ZPL', 'EPL2', or 'PNG'.
	 * @example ```"PDF"```
	 */
	label_format?: string | null
	/** 
	 * Flag it if you want to skip the electronic invoice creation when generating the customs info for this stock location shipments.
	 */
	suppress_etd?: boolean | null

	address?: Address | null
	inventory_stock_locations?: InventoryStockLocation[] | null
	inventory_return_locations?: InventoryReturnLocation[] | null
	stock_items?: StockItem[] | null
	stock_transfers?: StockTransfer[] | null
	stores?: Store[] | null
	attachments?: Attachment[] | null
	versions?: Version[] | null
	event_stores?: EventStore[] | null

}


interface StockLocationCreate extends ResourceCreate {
	
	/** 
	 * The stock location's internal name.
	 * @example ```"Primary warehouse"```
	 */
	name: string
	/** 
	 * A string that you can use to identify the stock location (must be unique within the environment).
	 * @example ```"europe1"```
	 */
	code?: string | null
	/** 
	 * The shipping label format for this stock location. Can be one of 'PDF', 'ZPL', 'EPL2', or 'PNG'.
	 * @example ```"PDF"```
	 */
	label_format?: string | null
	/** 
	 * Flag it if you want to skip the electronic invoice creation when generating the customs info for this stock location shipments.
	 */
	suppress_etd?: boolean | null

	address: AddressRel

}


interface StockLocationUpdate extends ResourceUpdate {
	
	/** 
	 * The stock location's internal name.
	 * @example ```"Primary warehouse"```
	 */
	name?: string | null
	/** 
	 * A string that you can use to identify the stock location (must be unique within the environment).
	 * @example ```"europe1"```
	 */
	code?: string | null
	/** 
	 * The shipping label format for this stock location. Can be one of 'PDF', 'ZPL', 'EPL2', or 'PNG'.
	 * @example ```"PDF"```
	 */
	label_format?: string | null
	/** 
	 * Flag it if you want to skip the electronic invoice creation when generating the customs info for this stock location shipments.
	 */
	suppress_etd?: boolean | null

	address?: AddressRel | null

}


class StockLocations extends ApiResource<StockLocation> {

	static readonly TYPE: StockLocationType = 'stock_locations' as const

	async create(resource: StockLocationCreate, params?: QueryParamsRetrieve<StockLocation>, options?: ResourcesConfig): Promise<StockLocation> {
		return this.resources.create<StockLocationCreate, StockLocation>({ ...resource, type: StockLocations.TYPE }, params, options)
	}

	async update(resource: StockLocationUpdate, params?: QueryParamsRetrieve<StockLocation>, options?: ResourcesConfig): Promise<StockLocation> {
		return this.resources.update<StockLocationUpdate, StockLocation>({ ...resource, type: StockLocations.TYPE }, params, options)
	}

	async delete(id: string | ResourceId, options?: ResourcesConfig): Promise<void> {
		await this.resources.delete((typeof id === 'string')? { id, type: StockLocations.TYPE } : id, options)
	}

	async address(stockLocationId: string | StockLocation, params?: QueryParamsRetrieve<Address>, options?: ResourcesConfig): Promise<Address> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<Address>({ type: 'addresses' }, `stock_locations/${_stockLocationId}/address`, params, options) as unknown as Address
	}

	async inventory_stock_locations(stockLocationId: string | StockLocation, params?: QueryParamsList<InventoryStockLocation>, options?: ResourcesConfig): Promise<ListResponse<InventoryStockLocation>> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<InventoryStockLocation>({ type: 'inventory_stock_locations' }, `stock_locations/${_stockLocationId}/inventory_stock_locations`, params, options) as unknown as ListResponse<InventoryStockLocation>
	}

	async inventory_return_locations(stockLocationId: string | StockLocation, params?: QueryParamsList<InventoryReturnLocation>, options?: ResourcesConfig): Promise<ListResponse<InventoryReturnLocation>> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<InventoryReturnLocation>({ type: 'inventory_return_locations' }, `stock_locations/${_stockLocationId}/inventory_return_locations`, params, options) as unknown as ListResponse<InventoryReturnLocation>
	}

	async stock_items(stockLocationId: string | StockLocation, params?: QueryParamsList<StockItem>, options?: ResourcesConfig): Promise<ListResponse<StockItem>> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<StockItem>({ type: 'stock_items' }, `stock_locations/${_stockLocationId}/stock_items`, params, options) as unknown as ListResponse<StockItem>
	}

	async stock_transfers(stockLocationId: string | StockLocation, params?: QueryParamsList<StockTransfer>, options?: ResourcesConfig): Promise<ListResponse<StockTransfer>> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<StockTransfer>({ type: 'stock_transfers' }, `stock_locations/${_stockLocationId}/stock_transfers`, params, options) as unknown as ListResponse<StockTransfer>
	}

	async stores(stockLocationId: string | StockLocation, params?: QueryParamsList<Store>, options?: ResourcesConfig): Promise<ListResponse<Store>> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<Store>({ type: 'stores' }, `stock_locations/${_stockLocationId}/stores`, params, options) as unknown as ListResponse<Store>
	}

	async attachments(stockLocationId: string | StockLocation, params?: QueryParamsList<Attachment>, options?: ResourcesConfig): Promise<ListResponse<Attachment>> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<Attachment>({ type: 'attachments' }, `stock_locations/${_stockLocationId}/attachments`, params, options) as unknown as ListResponse<Attachment>
	}

	async versions(stockLocationId: string | StockLocation, params?: QueryParamsList<Version>, options?: ResourcesConfig): Promise<ListResponse<Version>> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<Version>({ type: 'versions' }, `stock_locations/${_stockLocationId}/versions`, params, options) as unknown as ListResponse<Version>
	}

	async event_stores(stockLocationId: string | StockLocation, params?: QueryParamsList<EventStore>, options?: ResourcesConfig): Promise<ListResponse<EventStore>> {
		const _stockLocationId = (stockLocationId as StockLocation).id || stockLocationId as string
		return this.resources.fetch<EventStore>({ type: 'event_stores' }, `stock_locations/${_stockLocationId}/event_stores`, params, options) as unknown as ListResponse<EventStore>
	}


	isStockLocation(resource: any): resource is StockLocation {
		return resource.type && (resource.type === StockLocations.TYPE)
	}


	relationship(id: string | ResourceId | null): StockLocationRel {
		return super.relationshipOneToOne<StockLocationRel>(id)
	}

	relationshipToMany(...ids: string[]): StockLocationRel[] {
		return super.relationshipOneToMany<StockLocationRel>(...ids)
	}


	type(): StockLocationType {
		return StockLocations.TYPE
	}

}


const instance = new StockLocations()
export default instance

export type { StockLocation, StockLocationCreate, StockLocations, StockLocationType, StockLocationUpdate }
